import { createRef } from "react";

import ArrowIcon from "@/design-system/icons/arrow-icon";
import { Link } from "@Components/Link";
import { onDefaultKeyboardUp } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import type { IComponentAjax, IComponentAjaxState } from "../ComponentAjax";
import { ComponentAjax } from "../ComponentAjax";
import { Loading } from "../Loading";
import { colors } from "./../../../styles/tokens.stylex";

import * as css from "./MultipleSelect.module.css";

export interface IMultipleSelect extends IComponentAjax<IMultipleSelectOption[]> {
    options: IMultipleSelectOption[];
    watermarkText: string;
    noResultsText: string;
    onChange: (selectedValues: IMultipleSelectOption[]) => void;
    selectedValues: IMultipleSelectOption[];
}

interface IMultipleSelectState extends IComponentAjaxState {
    options: IMultipleSelectOption[];
    dropDownIsOpen: boolean;
}

export interface IMultipleSelectOption {
    text: string;
    value: string;
}

export class MultipleSelect extends ComponentAjax<
    IMultipleSelectOption[],
    IMultipleSelect,
    IMultipleSelectState
> {
    private selectedValues: IMultipleSelectOption[];

    private dropdownData: IMultipleSelectOption[];

    private dropdownIndex: number = -1;

    private inputSearchBox = createRef<HTMLInputElement>();

    private resultsList = createRef<HTMLDivElement>();

    constructor(props: IMultipleSelect) {
        super(props);

        this.state = {
            isLoading: (!props.options || props.options.length === 0) && this.props.ajaxAction !== null,
            options: this.props.options ? this.props.options : [],
            dropDownIsOpen: false,
        };

        this.selectedValues = this.props.selectedValues ? this.props.selectedValues : [];
        this.dropdownData = this.props.options ? this.props.options : [];
    }

    public componentDidMount() {
        if (this.state.isLoading) {
            this.ajaxRequest();
        }
    }

    public render() {
        const toggleDropdown = () => this.toggleDropdown(!this.state.dropDownIsOpen);
        return (
            /* eslint-disable jsx-a11y/no-static-element-interactions */
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div className={css.multipleSelectComponent} onClick={toggleDropdown}>
                <div className={css.dropDown}>
                    <ArrowIcon direction="down" styleX={styles.dropDownIcon} />
                </div>
                {this.renderItems()}
                <input
                    className={css.inputSearchBox}
                    type="text"
                    ref={this.inputSearchBox}
                    onChange={this.updateBrands}
                    style={
                        this.inputSearchBox === null
                            ? undefined
                            : this.inputSearchBox && this.selectedValues.length !== 0
                              ? { maxWidth: "95%", width: "16px" }
                              : { width: "85%" }
                    }
                    placeholder={this.selectedValues.length === 0 ? this.props.watermarkText : undefined}
                    onKeyDown={this.onKeyDown}
                />
                <hr />
                {this.state.dropDownIsOpen ? this.renderOptions() : null}
            </div>
        );
    }

    protected ajaxSuccess(response: IMultipleSelectOption[]): void {
        this.dropdownData = response;
        this.setState({ isLoading: false, options: response });
    }

    protected ajaxFail(): boolean {
        this.setState(() => ({ isLoading: false }));
        return true;
    }

    private onChange = () => {
        if (this.props.onChange) {
            this.props.onChange.call(this, this.selectedValues);
        }
    };

    private toggleDropdown(shouldOpen: boolean) {
        if (this.state.dropDownIsOpen === false || shouldOpen === true) {
            this.setState({ dropDownIsOpen: true });

            if (this.inputSearchBox.current) {
                this.inputSearchBox.current.focus();
            }

            document.body.addEventListener("click", this.handleClick, false);
            this.dropdownIndex = -1;
            this.updateBrands();
        } else {
            this.setState({ dropDownIsOpen: false });
            document.body.removeEventListener("click", this.handleClick, false);
        }
    }

    private updateBrands = () => {
        const newOptions = [];
        for (let i = 0; i < this.dropdownData.length; i += 1) {
            const item = this.dropdownData[i];
            let notSelected = true;
            for (let z = 0; z < this.selectedValues.length && notSelected; z += 1) {
                if (this.selectedValues[z].value.localeCompare(item.value) === 0) {
                    notSelected = false;
                }
            }
            if (
                notSelected &&
                this.inputSearchBox.current &&
                (this.inputSearchBox.current.value.length === 0 ||
                    item.text.toLowerCase().indexOf(this.inputSearchBox.current.value.toLowerCase()) !== -1)
            ) {
                newOptions.push(item);
            }
        }
        this.dropdownIndex = -1;
        this.setState({ options: newOptions });
        if (this.inputSearchBox.current) {
            this.inputSearchBox.current.style.width =
                this.selectedValues.length > 0 || this.inputSearchBox.current.value.length > 0
                    ? `${(this.inputSearchBox.current.value.length + 2) * 10}px`
                    : "85%";
            this.inputSearchBox.current.maxLength =
                newOptions.length === 0
                    ? this.inputSearchBox.current.value.length
                    : this.inputSearchBox.current.value.length + 1;
        }
    };

    private selectItem = (obj: IMultipleSelectOption, index: number) => {
        this.selectedValues.push(obj);
        this.setState((state) => {
            const newOptionsState = state.options;
            newOptionsState.splice(index, 1);
            return { options: newOptionsState };
        });

        if (this.inputSearchBox.current) {
            this.inputSearchBox.current.value = "";
        }

        this.onChange();
    };

    private removeItem = (e: React.SyntheticEvent<HTMLElement>, index: number) => {
        e.stopPropagation();
        this.selectedValues.splice(index, 1);
        this.updateBrands();
        this.onChange();
    };

    private createDropdownItem = (currentValue: IMultipleSelectOption, index: number) => {
        const onClick = () => this.selectItem(currentValue, index);

        return (
            <div
                className={css.scrollbarItem}
                id={currentValue.value}
                key={index}
                onClick={onClick}
                role="button"
                onKeyUp={onDefaultKeyboardUp}
                tabIndex={index}
            >
                {currentValue.text}
            </div>
        );
    };

    private updateDropdownList = (): void => {
        const index = this.dropdownIndex >= 0 ? this.dropdownIndex : 0;
        if (this.resultsList.current) {
            const scrollBarPositioning = this.resultsList.current.getBoundingClientRect();
            const selectedItem = this.resultsList.current.children[index].getBoundingClientRect();

            if (selectedItem.top - scrollBarPositioning.top < 0) {
                this.resultsList.current.scrollTop += selectedItem.top - scrollBarPositioning.top;
            } else if (selectedItem.bottom - scrollBarPositioning.bottom >= 0) {
                this.resultsList.current.scrollTop += selectedItem.bottom - scrollBarPositioning.bottom;
            }
        }
    };

    private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.keyCode === 27 && this.state.dropDownIsOpen) {
            e.stopPropagation();
            e.preventDefault();
            this.toggleDropdown(false);
        } else if (!this.state.dropDownIsOpen) {
            this.toggleDropdown(true);
            if (e.keyCode === 40 || e.keyCode === 38) {
                return;
            }
        }

        if (!this.inputSearchBox.current || !this.resultsList.current) {
            return;
        }

        if (e.keyCode === 8 && this.inputSearchBox && this.inputSearchBox.current.value.length === 0) {
            e.stopPropagation();
            e.preventDefault();
            if (this.selectedValues.length > 0) {
                this.removeItem(e, this.selectedValues.length - 1);
            }
        } else if (this.state.options.length > 0) {
            if (e.keyCode === 40 || e.keyCode === 38 || e.keyCode === 34 || e.keyCode === 33) {
                e.stopPropagation();
                e.preventDefault();

                if (this.dropdownIndex !== -1 && this.resultsList) {
                    this.resultsList.current.children[this.dropdownIndex].classList.remove("Selected");
                }

                if (
                    (e.keyCode === 40 || e.keyCode === 34) &&
                    this.resultsList.current.children[this.dropdownIndex + 1]
                ) {
                    if (e.keyCode === 34) {
                        this.dropdownIndex += 8;
                        if (this.dropdownIndex > this.state.options.length) {
                            this.dropdownIndex = this.state.options.length - 1;
                        }
                    } else {
                        this.dropdownIndex += 1;
                    }
                } else if (
                    (e.keyCode === 38 || e.keyCode === 33) &&
                    this.resultsList.current.children[this.dropdownIndex]
                ) {
                    if (e.keyCode === 33) {
                        this.dropdownIndex -= 8;
                        if (this.dropdownIndex < 0) {
                            this.dropdownIndex = -1;
                        }
                    } else {
                        this.dropdownIndex -= 1;
                    }
                }

                if (this.dropdownIndex >= 0) {
                    this.resultsList.current.children[this.dropdownIndex].classList.add("Selected");
                }

                this.updateDropdownList();
            } else if (e.keyCode === 13) {
                if (this.dropdownIndex >= 0) {
                    e.stopPropagation();
                    e.preventDefault();
                    (this.resultsList.current.children[this.dropdownIndex] as HTMLDivElement).click();
                } else if (this.dropdownIndex < 0 && this.resultsList) {
                    e.stopPropagation();
                    e.preventDefault();
                    (this.resultsList.current.children[0] as HTMLDivElement).click();
                }
            } else if (this.dropdownIndex >= 0) {
                this.resultsList.current.children[this.dropdownIndex].classList.remove("Selected");
                this.dropdownIndex = -1;
                this.resultsList.current.scrollTop = 0;
            }
        }
    };

    private handleClick = (e: MouseEvent) => {
        if (!e.target) {
            return;
        }

        const { className } = e.target as HTMLElement;
        if (
            className !== css.closeIcon &&
            className !== css.multipleSelectComponent &&
            className !== css.inputSearchBox &&
            className !== css.scrollbar &&
            className !== css.scrollbarItem
        ) {
            this.toggleDropdown(false);
        }
    };

    private renderOptions = (): JSX.Element =>
        this.state.isLoading === true ? (
            <Loading />
        ) : (
            <div className={css.scrollbar} ref={this.resultsList}>
                {this.props.options.length === 0
                    ? this.props.noResultsText
                    : this.state.options.map(this.createDropdownItem)}
            </div>
        );

    private renderItems(): JSX.Element[] {
        return this.selectedValues.map((currentValue, index) => {
            const removeItem = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
                this.removeItem(e, index);

            return (
                <span className={css.multipleSelectItem} key={currentValue.text}>
                    <h6 className={css.text}>{currentValue.text}</h6>
                    <Link onClick={removeItem}>
                        <span className={css.closeIcon}>&times;</span>
                    </Link>
                </span>
            );
        });
    }
}

const styles = stylex.create({
    dropDownIcon: {
        float: "right",
        display: "inline-block",
        width: 13,
        height: 10,
        zoom: 1,
        color: colors.b_BrandNavy,
    },
});
